import type { ColumnDef } from '@tanstack/react-table'

import type { MetadataDef } from 'types/filter-page'
import type { Track } from 'types/track'

import { defaultColumnConfig } from './config'
import type { ColumnTypeConfig } from './types'

const defaultAccessorKey = (metadataDef: MetadataDef) => {
  if (metadataDef.field === 'id') {
    return metadataDef.field
  }

  // By default we use the metadata field as the accessor key
  // This is because that's where most of the columns data is in
  return `metadata.${metadataDef.field}`
}

export const createColumn = (
  metadataDef: MetadataDef,
  columnTypes: ColumnTypeConfig<Track>[],
): ColumnDef<Track> => {
  const columnConfig =
    columnTypes.find(item => item.matcher(metadataDef)) ?? defaultColumnConfig

  const resolveAccessorKey = (): string | null => {
    if ('accessorFn' in columnConfig && columnConfig.accessorFn) return null
    if ('isDisplayColumn' in columnConfig && columnConfig.isDisplayColumn)
      return null
    const fn =
      'accessorKey' in columnConfig && columnConfig.accessorKey
        ? columnConfig.accessorKey
        : defaultAccessorKey
    return fn(metadataDef)
  }
  const accessorKey = resolveAccessorKey()

  const baseDef = {
    id: columnConfig.id ?? metadataDef.field,
    header: ctx => columnConfig.header?.(metadataDef, ctx) ?? metadataDef.label,
    cell: info => columnConfig.cell(metadataDef, info),
    size: columnConfig.size ?? 50,
    minSize: columnConfig.minSize ?? 50,
    enableSorting: columnConfig.enableSorting ?? true,
    ...(columnConfig.enableResizing !== undefined && {
      enableResizing: columnConfig.enableResizing,
    }),
    enableHiding: columnConfig.enableHiding ?? true,
    meta: {
      ...columnConfig.meta,
      metadataDef,
    },
  } satisfies Partial<ColumnDef<Track>>

  if (columnConfig.accessorFn) {
    return { ...baseDef, accessorFn: columnConfig.accessorFn }
  }

  return {
    ...baseDef,
    accessorKey: accessorKey ?? defaultAccessorKey(metadataDef),
  }
}
